"use client";

import { useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import {
  CoordinatePlane,
  PLOT_HEIGHT,
  PLOT_WIDTH,
  PlanePoint,
} from "@/components/tools/math/coordinate-plane";
import { useToolNumber } from "@/components/tools/tool-ui";
import { downloadPng } from "@/lib/tool-kit/download";
import { equalizeAspect, padRange } from "@/lib/tool-kit/math/plot";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/**
 * A point on the plane with its radius drawn from the origin, the circle it sits on and
 * the angle from the positive x-axis, so the quadrant the angle lands in can be seen.
 */
export function PolarPlot({
  x,
  y,
  pointLabel,
  angleLabel,
  title,
  downloadLabel,
}: {
  x: number;
  y: number;
  pointLabel: string;
  /** Text for the angle arc, e.g. "135°". */
  angleLabel: string;
  title: string;
  downloadLabel: string;
}) {
  const fmt = useToolNumber();
  const ref = useRef<SVGSVGElement>(null);
  const radius = Math.hypot(x, y);
  const reach = Math.max(radius, 1);
  const [xMin, xMax] = padRange(-reach, reach, 0.2, 2);
  const [yMin, yMax] = padRange(-reach, reach, 0.2, 2);
  const viewport = equalizeAspect(
    { xMin, xMax, yMin, yMax },
    PLOT_WIDTH,
    PLOT_HEIGHT
  );
  const degrees = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  return (
    <div className="space-y-3">
      <CoordinatePlane
        formatTick={(value, decimals) =>
          fmt(value, { maximumFractionDigits: decimals })
        }
        svgRef={ref}
        title={title}
        viewport={viewport}
      >
        {(scale) => {
          const cx = scale.x(0);
          const cy = scale.y(0);
          const unit = scale.x(1) - scale.x(0);
          const arcRadius = Math.min(radius * unit * 0.4, 46);
          const theta = (degrees * Math.PI) / 180;
          const arcEnd = {
            x: cx + arcRadius * Math.cos(theta),
            y: cy - arcRadius * Math.sin(theta),
          };
          const mid = {
            x: cx + (arcRadius + 18) * Math.cos(theta / 2),
            y: cy - (arcRadius + 18) * Math.sin(theta / 2),
          };
          return (
            <>
              {radius > 0 ? (
                <>
                  <circle
                    cx={cx}
                    cy={cy}
                    fill="none"
                    r={radius * unit}
                    stroke={CHART_COLORS[4]}
                    strokeDasharray="5 4"
                    strokeWidth={1.5}
                  />
                  <line
                    stroke={CHART_COLORS[0]}
                    strokeWidth={3}
                    x1={cx}
                    x2={scale.x(x)}
                    y1={cy}
                    y2={scale.y(y)}
                  />
                  {degrees > 0 ? (
                    <>
                      <path
                        d={`M${cx + arcRadius} ${cy} A${arcRadius} ${arcRadius} 0 ${degrees > 180 ? 1 : 0} 0 ${arcEnd.x} ${arcEnd.y}`}
                        fill="none"
                        stroke={CHART_COLORS[3]}
                        strokeWidth={2}
                      />
                      <text
                        dominantBaseline="middle"
                        fill={CHART_INK.text}
                        fontSize={12}
                        fontWeight={600}
                        textAnchor="middle"
                        x={mid.x}
                        y={mid.y}
                      >
                        {angleLabel}
                      </text>
                    </>
                  ) : null}
                </>
              ) : null}
              <PlanePoint
                color={CHART_COLORS[3]}
                cx={scale.x(x)}
                cy={scale.y(y)}
                label={pointLabel}
              />
            </>
          );
        }}
      </CoordinatePlane>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng("polar-point.png", ref.current).catch(() => undefined);
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}
